import CollapsibleSection from "components/Section";
import { Button } from "components/Button";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { TUser, UserType } from "types/dto";
import { Controller, useForm } from "react-hook-form";
import { TextField } from "@mui/material";
import { Select } from "components/Select";
import RegionAutoComplete from "components/SpecificAutoComplete/RegionAutoComplete";
import { SortObjectByPropName } from "utils/arrayTools";
import { Enum2Array } from "utils/enumTools";
import { Permission } from "components/Permission";

type TProps = {
  data?: TUser;
  isLoading: boolean;
  onSaveClick: (e: TEditFormTypes) => void;
  onEditClick: () => void;
  isButtonLoading: boolean;
  enableEditing: boolean;
  onResetPasswordClick: () => void;
};
export type TEditFormTypes = {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  provinceId: string;
  cityId: string;
  jurisdictionId: string;
  userType: UserType;
};

const userTypeOptions = SortObjectByPropName(Enum2Array(UserType), "name");

export const UserInfo = (props: TProps) => {
  const {
    data,
    isLoading,
    onSaveClick,
    isButtonLoading,
    enableEditing,
    onEditClick,
    onResetPasswordClick,
  } = props;

  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    getValues,
  } = useForm<TEditFormTypes>({
    defaultValues: undefined,
  });

  useEffect(() => {
    if (data) {
      setValue("username", data.username);
      setValue("email", data.email);
      setValue("firstName", data.firstName);
      setValue("lastName", data.lastName);
      setValue("provinceId", data.provinceId);
      setValue("cityId", data.cityId);
      setValue("jurisdictionId", data.jurisdictionId);
      setValue("userType", data.userType);
    }
  }, [data]);

  return (
    <CollapsibleSection
      title={t("userInfo")}
      collapsible={false}
      isLoading={isLoading}
      skeleton="text"
    >
      <div className="flex flex-col w-full gap-y-3">
        <div className={"flex w-full gap-x-2"}>
          <Controller
            name={"firstName"}
            control={control}
            rules={{
              required: true,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  label={t("firstName")}
                  placeholder={t("firstName")}
                  size="small"
                  {...field}
                  error={Boolean(error)}
                  className={"flex-1"}
                  InputLabelProps={{
                    shrink: !!field.value,
                  }}
                  disabled={!enableEditing}
                />
              );
            }}
          />
          <Controller
            name={"lastName"}
            control={control}
            rules={{
              required: true,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  label={t("lastName")}
                  placeholder={t("lastName")}
                  size="small"
                  {...field}
                  error={Boolean(error)}
                  className={"flex-1"}
                  InputLabelProps={{
                    shrink: !!field.value,
                  }}
                  disabled={!enableEditing}
                />
              );
            }}
          />
        </div>
        <div className={"flex w-full gap-x-2"}>
          <Controller
            name={"username"}
            control={control}
            rules={{
              required: true,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  label={t("username")}
                  placeholder={t("username")}
                  size="small"
                  {...field}
                  error={Boolean(error)}
                  className={"w-1/2"}
                  InputLabelProps={{
                    shrink: !!field.value,
                  }}
                  disabled={!enableEditing}
                />
              );
            }}
          />
          <Controller
            name={"email"}
            control={control}
            rules={{
              required: true,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  label={t("email")}
                  placeholder={t("email")}
                  size="small"
                  {...field}
                  error={Boolean(error)}
                  className={"w-1/2"}
                  InputLabelProps={{
                    shrink: !!field.value,
                  }}
                  disabled={!enableEditing}
                />
              );
            }}
          />
        </div>
        <div className="grid grid-cols-2 gap-x-2">
          <Controller
            name={"userType"}
            control={control}
            rules={{
              required: true,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <Select
                  size="small"
                  className="col-span-1"
                  label={t("userType")}
                  options={userTypeOptions}
                  onChange={(e) => {
                    field.onChange(e?.value);
                  }}
                  initialValue={userTypeOptions.find(
                    (item) => (item.value as unknown as UserType) == field.value
                  )}
                  disabled={!enableEditing}
                />
              );
            }}
          />
        </div>
        <div className={"flex w-full gap-x-2"}>
          <RegionAutoComplete
            defaultValue={{
              provinceId: getValues("provinceId"),
              cityId: getValues("cityId"),
              jurisdictionId: getValues("jurisdictionId"),
            }}
            onChange={(value) => {
              setValue("provinceId", value?.province?.id || "");
              setValue("cityId", value?.city?.id || "");
              setValue("jurisdictionId", value?.jurisdiction?.id || "");
            }}
            disabled={!enableEditing}
          />
        </div>
      </div>
      <div className="w-full flex items-center justify-end mt-4 gap-x-2">
        {enableEditing && (
          <Permission allowed={["superadmin", "d_user"]}>
            <Button
              variant={"outlined"}
              color={"warning"}
              onClick={() => {
                onResetPasswordClick();
              }}
              loading={isButtonLoading}
              type={enableEditing ? "submit" : "button"}
            >
              {t("resetPassword")}
            </Button>
          </Permission>
        )}
        <Permission allowed={["superadmin", "d_user"]}>
          <Button
            variant={enableEditing ? "contained" : "outlined"}
            onClick={() => {
              if (enableEditing) {
                handleSubmit(onSaveClick)();
              } else {
                onEditClick();
              }
            }}
            loading={isButtonLoading}
            type={enableEditing ? "submit" : "button"}
          >
            {enableEditing ? t("save") : t("edit")}
          </Button>
        </Permission>
      </div>
    </CollapsibleSection>
  );
};
